import React, { useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Popconfirm,
  Typography,
  Select,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  ShopOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useDealerPackages } from '../hooks';
import { DealerPackage } from '../../api/services/adminService';

const { Title, Text } = Typography;
const ORANGE = '#fe5a1d';

const DealerPackages: React.FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const {
    packages,
    loading,
    modalVisible,
    editingPackage,
    submitting,
    setModalVisible,
    handleCreate,
    handleUpdate,
    handleDelete,
    openCreateModal,
    openEditModal,
    refetch,
  } = useDealerPackages();

  useEffect(() => {
    if (modalVisible && editingPackage) {
      form.setFieldsValue({
        name: editingPackage.name,
        description: editingPackage.description,
        price: editingPackage.price,
        currency: editingPackage.currency ?? 'ALL',
        maxListings: editingPackage.maxListings,
        sortOrder: editingPackage.sortOrder,
        isActive: editingPackage.isActive,
        features: editingPackage.features?.join('\n') ?? '',
      });
    } else if (modalVisible && !editingPackage) {
      form.resetFields();
      form.setFieldsValue({ isActive: true, sortOrder: 0, currency: 'ALL' });
    }
  }, [modalVisible, editingPackage, form]);

  const onFinish = (values: any) => {
    const raw = {
      ...values,
      features: values.features
        ? values.features.split('\n').map((f: string) => f.trim()).filter(Boolean)
        : [],
    };
    const payload = Object.fromEntries(
      Object.entries(raw).filter(([, v]) => v !== null && v !== undefined),
    );
    if (editingPackage) {
      handleUpdate(editingPackage.id, payload);
    } else {
      handleCreate(payload as any);
    }
  };

  const columns = [
    {
      title: t('admin.dealer_packages.col_package'),
      dataIndex: 'name',
      key: 'name',
      render: (v: string) => (
        <Space>
          <ShopOutlined style={{ color: ORANGE }} />
          <Text strong>{v}</Text>
        </Space>
      ),
    },
    {
      title: t('admin.dealer_packages.col_description'),
      dataIndex: 'description',
      key: 'description',
      render: (v: string) => <Text type="secondary">{v ?? '—'}</Text>,
    },
    {
      title: t('admin.dealer_packages.col_price'),
      dataIndex: 'price',
      key: 'price',
      render: (v: number, rec: DealerPackage) => (
        <Text strong style={{ color: ORANGE }}>
          {Number(v).toLocaleString()} {rec.currency}
        </Text>
      ),
    },
    {
      title: t('admin.dealer_packages.col_max_listings'),
      dataIndex: 'maxListings',
      key: 'maxListings',
      render: (v: number | null) => (
        <Text>{v != null ? v : t('admin.dealer_packages.unlimited')}</Text>
      ),
    },
    {
      title: t('admin.dealer_packages.col_sort'),
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      render: (v: number) => <Text type="secondary">{v}</Text>,
    },
    {
      title: t('admin.dealer_packages.col_status'),
      dataIndex: 'isActive',
      key: 'isActive',
      render: (v: boolean) => (
        <Tag color={v ? 'green' : 'default'}>{v ? t('admin.dealer_packages.status_active') : t('admin.dealer_packages.status_inactive')}</Tag>
      ),
    },
    {
      title: t('admin.dealer_packages.col_actions'),
      key: 'actions',
      render: (_: any, record: DealerPackage) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
            style={{ color: ORANGE }}
          />
          <Popconfirm
            title={t('admin.dealer_packages.delete_confirm')}
            description={t('admin.dealer_packages.delete_desc')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('admin.dealer_packages.btn_delete')}
            okType="danger"
            cancelText={t('admin.dealer_packages.btn_cancel')}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>{t('admin.dealer_packages.title')}</Title>
          <Text type="secondary">{t('admin.dealer_packages.subtitle')}</Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={refetch} loading={loading} className="admin-filter-btn">
            {t('admin.dealer_packages.btn_refresh')}
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreateModal}
            className="ant-btn-primary admin-filter-btn admin-filter-btn-primary"
          >
            {t('admin.dealer_packages.btn_add')}
          </Button>
        </Space>
      </div>

      <Card bordered={false} className="admin-table-card">
        <Table
          rowKey="id"
          loading={loading}
          dataSource={packages}
          columns={columns}
          pagination={false}
          locale={{ emptyText: t('admin.dealer_packages.empty_text') }}
        />
      </Card>

      <Modal
        title={
          <Space>
            <ShopOutlined style={{ color: 'var(--admin-primary)' }} />
            {editingPackage ? t('admin.dealer_packages.modal_edit_title') : t('admin.dealer_packages.modal_add_title')}
          </Space>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        confirmLoading={submitting}
        onOk={() => form.submit()}
        okText={editingPackage ? t('admin.dealer_packages.modal_save') : t('admin.dealer_packages.modal_create')}
        okButtonProps={{ className: 'ant-btn-primary' }}
        cancelText={t('admin.dealer_packages.btn_cancel')}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onFinish} style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label={t('admin.dealer_packages.label_name')}
            rules={[{ required: true, message: t('admin.dealer_packages.validation_name') }]}
          >
            <Input placeholder={t('admin.dealer_packages.ph_name')} />
          </Form.Item>
          <Form.Item name="description" label={t('admin.dealer_packages.label_description')}>
            <Input.TextArea rows={2} placeholder={t('admin.dealer_packages.ph_description')} />
          </Form.Item>
          <Form.Item
            name="price"
            label={t('admin.dealer_packages.label_price')}
            rules={[{ required: true, message: t('admin.dealer_packages.validation_price') }]}
          >
            <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder={t('admin.dealer_packages.ph_price')} />
          </Form.Item>
          <Form.Item name="currency" label={t('admin.dealer_packages.label_currency')}>
            <Select>
              <Select.Option value="ALL">{t('admin.dealer_packages.currency_all_label')}</Select.Option>
              <Select.Option value="EUR">EUR</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="maxListings" label={t('admin.dealer_packages.label_max_listings')}>
            <InputNumber min={0} style={{ width: '100%' }} placeholder={t('admin.dealer_packages.ph_max_listings')} />
          </Form.Item>
          <Form.Item
            name="features"
            label={t('admin.dealer_packages.label_features')}
          >
            <Input.TextArea
              rows={4}
              placeholder={t('admin.dealer_packages.ph_features')}
            />
          </Form.Item>
          <Form.Item name="sortOrder" label={t('admin.dealer_packages.label_sort_order')}>
            <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
          </Form.Item>
          <Form.Item name="isActive" label={t('admin.dealer_packages.label_active')} valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default DealerPackages;
